/** @jsxImportSource @emotion/react */
import { Theme } from "@emotion/react";
import { ReactComponent as UnstyledIconArrow } from "./arrow.svg";
import { ReactComponent as UnstyledCircleArrow } from "./arrow-circle.svg";
import { ReactComponent as UnstyledOutlineArrow } from "./arrow-outline.svg";
import { ReactComponent as Bag } from "./shopping.svg";
import { ReactComponent as Bell } from "./bell.svg";
import { ReactComponent as Bitcoin } from "./bitcoin.svg";
import { ReactComponent as Calendar } from "./calendar.svg";
import { ReactComponent as Chat } from "./chat.svg";
import { ReactComponent as ChatHeart } from "./chat-heart.svg";
import { ReactComponent as ChatPencil } from "./chat-pencil.svg";
import { ReactComponent as Chevron } from "./chevron.svg";
import { ReactComponent as Confetti } from "./confetti.svg";
import { ReactComponent as Copy } from "./copy.svg";
import { ReactComponent as Clock } from "./clock.svg";
import { ReactComponent as CloudDownload } from "./cloud-download.svg";
import { ReactComponent as Crown } from "./crown.svg";
import { ReactComponent as Diamond } from "./diamond.svg";
import { ReactComponent as Discord } from "./discord.svg";
import { ReactComponent as Ethereum } from "./ethereum.svg";
import { ReactComponent as EnvelopeHeart } from "./envelope-heart.svg";
import { ReactComponent as Eye } from "./eye.svg";
import { ReactComponent as ExternalLink } from "./external_link.svg";
import { ReactComponent as Expand } from "./expand.svg";
import { ReactComponent as Filter } from "./filter.svg";
import { ReactComponent as Fire } from "./fire.svg";
import { ReactComponent as Globe } from "./globe.svg";
import { ReactComponent as GlobeAlt } from "./globe-alt.svg";
import { ReactComponent as GraduationCap } from "./graduation-cap.svg";
import { ReactComponent as Hearts } from "./hearts.svg";
import { ReactComponent as HugLogo } from "./HugLogo.svg";
import { ReactComponent as Image } from "./image.svg";
import { ReactComponent as ImageAlt1 } from "./image-alt-1.svg";
import { ReactComponent as ImageAlt2 } from "./image-alt-2.svg";
import { ReactComponent as Instagram } from "./instagram.svg";
import { ReactComponent as Keys } from "./keys.svg";
import { ReactComponent as Link } from "./link.svg";
import { ReactComponent as MapMarker } from "./map_marker.svg";
import { ReactComponent as Megaphone } from "./megaphone.svg";
import { ReactComponent as Minus } from "./minus.svg";
import { ReactComponent as MinusThick } from "./minus-thick.svg";
import { ReactComponent as OpenSea } from "./opensea.svg";
import { ReactComponent as Palette } from "./palette.svg";
import { ReactComponent as Pause } from "./pause.svg";
import { ReactComponent as Play } from "./play.svg";
import { ReactComponent as Plus } from "./plus-thick.svg";
import { ReactComponent as PlusThick } from "./plus.svg";
import { ReactComponent as RaisedHands } from "./raised-hands.svg";
import { ReactComponent as Reddit } from "./reddit.svg";
import { ReactComponent as Search } from "./magnifying-glass.svg";
import { ReactComponent as Share } from "./share.svg";
import { ReactComponent as Stars } from "./stars.svg";
import { ReactComponent as Twitter } from "./twitter.svg";
import { ReactComponent as TrashCan } from "./trash-can.svg";
import { ReactComponent as User } from "./user.svg";
import { ReactComponent as Users } from "./users.svg";
import { ReactComponent as Website } from "./website.svg";
import { ReactComponent as Video } from "./video.svg";
import { ReactComponent as X } from "./x_rounded.svg";

interface IconColorProps extends React.HTMLAttributes<SVGElement> {
  accent?: string | undefined;
  primary?: "fg" | "bg" | "white" | "black";
  size?: string;
}

const colorStyles =
  ({ primary, accent, size = "1em" }: IconColorProps) =>
  (theme: Theme) => ({
    color: primary ? theme.colors[primary] : "currentColor",
    "& .accent": {
      ...(accent && {
        fill: theme.colors[accent],
      }),
    },
    display: "inline-block",
    ...(size in theme.fontSizes
      ? {
          width: "1em",
          fontSize: theme.fontSizes[size as keyof Theme["fontSizes"]],
        }
      : {
          width: size ?? "1em",
        }),
    height: "auto",
  });

const withColorProps = <TProps extends object>(
  Component: React.ComponentType<TProps>
) => {
  function ColoredComponent({
    primary,
    accent,
    size,
    ...props
  }: TProps & IconColorProps) {
    return (
      <Component
        css={colorStyles({ primary, accent, size })}
        {...(props as TProps)}
      />
    );
  }

  return ColoredComponent;
};

const IconArrowAlt = withColorProps(IconArrow);
const IconBag = withColorProps(Bag);
const IconBell = withColorProps(Bell);
const IconBitcoin = withColorProps(Bitcoin);
const IconCalendar = withColorProps(Calendar);
const IconChat = withColorProps(Chat);
const IconChatHeart = withColorProps(ChatHeart);
const IconChatPencil = withColorProps(ChatPencil);
const IconChevron = withColorProps(Chevron);
const IconClock = withColorProps(Clock);
const IconCopy = withColorProps(Copy);
const IconConfetti = withColorProps(Confetti);
const IconCrown = withColorProps(Crown);
const IconCloudDownload = withColorProps(CloudDownload);
const IconDiamond = withColorProps(Diamond);
const IconDiscord = withColorProps(Discord);
const IconEthereum = withColorProps(Ethereum);
const IconEnvelopeHeart = withColorProps(EnvelopeHeart);
const IconExternalLink = withColorProps(ExternalLink);
const IconExpand = withColorProps(Expand);
const IconEye = withColorProps(Eye);
const IconFilter = withColorProps(Filter);
const IconFire = withColorProps(Fire);
const IconGraduationCap = withColorProps(GraduationCap);
const IconGlobe = withColorProps(Globe);
const IconGlobeAlt = withColorProps(GlobeAlt);
const IconHearts = withColorProps(Hearts);
const IconHugLogo = withColorProps(HugLogo);
const IconImage = withColorProps(Image);
const IconImageAlt1 = withColorProps(ImageAlt1);
const IconImageAlt2 = withColorProps(ImageAlt2);
const IconInstagram = withColorProps(Instagram);
const IconKeys = withColorProps(Keys);
const IconLink = withColorProps(Link);
const IconMapMarker = withColorProps(MapMarker);
const IconMegaphone = withColorProps(Megaphone);
const IconMinus = withColorProps(Minus);
const IconMinusThick = withColorProps(MinusThick);
const IconOpenSea = withColorProps(OpenSea);
const IconPause = withColorProps(Pause);
const IconPlay = withColorProps(Play);
const IconPlus = withColorProps(Plus);
const IconPalette = withColorProps(Palette);
const IconPlusThick = withColorProps(PlusThick);
const IconRaisedHands = withColorProps(RaisedHands);
const IconReddit = withColorProps(Reddit);
const IconSearch = withColorProps(Search);
const IconShare = withColorProps(Share);
const IconStars = withColorProps(Stars);
const IconTrashCan = withColorProps(TrashCan);
const IconTwitter = withColorProps(Twitter);
const IconUser = withColorProps(User);
const IconUsers = withColorProps(Users);
const IconWebsite = withColorProps(Website);
const IconVideo = withColorProps(Video);
const IconX = withColorProps(X);

interface IconArrowProps extends React.HTMLAttributes<SVGElement> {
  dir?: "ul" | "u" | "ur" | "r" | "dr" | "d" | "dl" | "l";
  variant?: "default" | "circle" | "outline";
}

const iconArrowCSS = {
  ul: { transform: "rotate(225deg)" },
  u: { transform: "rotate(270deg)" },
  ur: { transform: "rotate(315deg)" },
  r: { transform: "rotate(0deg)" },
  dr: { transform: "rotate(45deg)" },
  d: { transform: "rotate(90deg)" },
  dl: { transform: "rotate(135deg)" },
  l: { transform: "rotate(180deg)" },
};

function IconArrow({
  dir = "r",
  variant = "default",
  ...props
}: IconArrowProps) {
  let Icon = UnstyledIconArrow;
  if (variant === "circle") {
    Icon = UnstyledCircleArrow;
  } else if (variant === "outline") {
    Icon = UnstyledOutlineArrow;
  }
  return <Icon css={[iconArrowCSS[dir]]} {...props} />;
}

// Font Icons, see this directory's readme.md for more info on usage
export type { IconArrowProps };
export {
  IconArrow,
  IconArrowAlt,
  IconBag,
  IconBell,
  IconBitcoin,
  IconCalendar,
  IconChat,
  IconClock,
  IconChatHeart,
  IconChatPencil,
  IconChevron,
  IconCloudDownload,
  IconConfetti,
  IconCopy,
  IconCrown,
  IconDiamond,
  IconDiscord,
  IconEnvelopeHeart,
  IconEthereum,
  IconExternalLink,
  IconExpand,
  IconEye,
  IconFilter,
  IconFire,
  IconGlobe,
  IconGlobeAlt,
  IconGraduationCap,
  IconHearts,
  IconHugLogo,
  IconImage,
  IconImageAlt1,
  IconImageAlt2,
  IconInstagram,
  IconKeys,
  IconLink,
  IconMapMarker,
  IconMegaphone,
  IconMinus,
  IconMinusThick,
  IconOpenSea,
  IconPalette,
  IconPause,
  IconPlay,
  IconPlus,
  IconPlusThick,
  IconRaisedHands,
  IconReddit,
  IconSearch,
  IconShare,
  IconStars,
  IconTrashCan,
  IconTwitter,
  IconUser,
  IconUsers,
  IconWebsite,
  IconVideo,
  IconX,
};
//
export { ReactComponent as IconAdd } from "./add.svg";
export { ReactComponent as IconAsync } from "./async.svg";
export { ReactComponent as IconApecoin } from "./apecoin.svg";
export { ReactComponent as IconAvalanche } from "./avalanche.svg";
export { ReactComponent as IconCardano } from "./cardano.svg";
export { ReactComponent as IconCelo } from "./celo.svg";
export { ReactComponent as IconCheck } from "./check.svg";
export { ReactComponent as IconCheckRounded } from "./check_rounded.svg";
export { ReactComponent as IconChevronBold } from "./chevron_bold.svg";
export { ReactComponent as IconClockElapsed } from "./clock-elapsed.svg";
export { ReactComponent as IconClose } from "./close.svg";
export { ReactComponent as IconCoinbase } from "./coinbase.svg";
export { ReactComponent as IconError } from "./error.svg";
export { ReactComponent as IconEtherscan } from "./etherscan.svg";
export { ReactComponent as IconEthereumLogo } from "./ethereumLogo.svg";
export { ReactComponent as IconEnvelopeSmallClosed } from "./envelope-sm-closed.svg";
export { ReactComponent as IconEnvelopeSmallOpen } from "./envelope-sm-open.svg";
export { ReactComponent as IconFeaturedCreator } from "./featured-creator.svg";
export { ReactComponent as IconFeaturedProject } from "./featured-project.svg";
export { ReactComponent as IconFlag } from "./flag.svg";
export { ReactComponent as IconFlagFill } from "./flag_fill.svg";
export { ReactComponent as IconFlow } from "./flow.svg";
export { ReactComponent as IconFoundation } from "./foundation.svg";
export { ReactComponent as IconInfo } from "./info.svg";
export { ReactComponent as IconImx } from "./imx.svg";
export { ReactComponent as IconKlaytn } from "./klaytn.svg";
export { ReactComponent as IconKusama } from "./kusama.svg";
export { ReactComponent as IconLinkedin } from "./linkedin.svg";
export { ReactComponent as IconMagicEden } from "./magic_eden.svg";
export { ReactComponent as IconMedium } from "./medium.svg";
export { ReactComponent as IconMenuSecondary } from "./menu_secondary.svg";
export { ReactComponent as IconMenu } from "./menu.svg";
export { ReactComponent as IconMetamask } from "./metamask.svg";
export { ReactComponent as IconOmnichain } from "./omnichain.svg";
export { ReactComponent as IconOfficialBadge } from "./official_badge.svg";
export { ReactComponent as IconOkx } from "./okx.svg";
export { ReactComponent as IconPencil } from "./pencil.svg";
export { ReactComponent as IconPolygon } from "./polygon.svg";
export { ReactComponent as IconReactionChefsKiss } from "./reaction_chefs-kiss.svg";
export { ReactComponent as IconReactionMeh } from "./reaction_meh.svg";
export { ReactComponent as IconReactionMehNoBg } from "./reaction_meh_no-bg.svg";
export { ReactComponent as IconReactionMehWhiteBg } from "./reaction_meh_white-bg.svg";
export { ReactComponent as IconReactionMustHug } from "./reaction_must-hug.svg";
export { ReactComponent as IconReactionMustHug2 } from "./reaction_must-hug-2.svg";
export { ReactComponent as IconReactionMustHugNoBg } from "./reaction_must-hug_no-bg.svg";
export { ReactComponent as IconReactionMustHugWhiteBg } from "./reaction_must-hug_white-bg.svg";
export { ReactComponent as IconReactionMindBlowing } from "./reaction_mind-blowing.svg";
export { ReactComponent as IconReactionNah } from "./reaction_nah.svg";
export { ReactComponent as IconReactionNahNoBg } from "./reaction_nah_no-bg.svg";
export { ReactComponent as IconReactionNahWhiteBg } from "./reaction_nah_white-bg.svg";
export { ReactComponent as IconReactionNeedItNow } from "./reaction_need-it-now.svg";
export { ReactComponent as IconReactionObsessed } from "./reaction_obsessed.svg";
export { ReactComponent as IconReactionOutOfThisWorld } from "./reaction_out-of-this-world.svg";
export { ReactComponent as IconReactionTakeMyMoney } from "./reaction_take-my-money.svg";
export { ReactComponent as IconReactionThoughtProvoking } from "./reaction_thought-provoking.svg";
export { ReactComponent as IconReactionYay } from "./reaction_yay.svg";
export { ReactComponent as IconReactionYayNoBg } from "./reaction_yay_no-bg.svg";
export { ReactComponent as IconReactionYayWhiteBg } from "./reaction_yay_white-bg.svg";
export { ReactComponent as IconSkip } from "./skip.svg";
export { ReactComponent as IconSolana } from "./solana.svg";
export { ReactComponent as IconTelegram } from "./telegram.svg";
export { ReactComponent as IconTikTok } from "./tiktok.svg";
export { ReactComponent as IconTezos } from "./tezos.svg";
export { ReactComponent as IconWalletConnect } from "./walletconnect.svg";
export { ReactComponent as IconYouTube } from "./youtube.svg";

export { ReactComponent as SliderWave } from "assets/images/slider-wave.svg";
